import React, { useMemo } from 'react';

import Pen from '@ast/magma/components/icon/icons/Pen';
import Star from '@ast/magma/components/icon/icons/Star';

import { Button } from '@ast/magma/components/button';
import { Menu } from '@ast/magma/components/menu';

import { PhoneSmsEnrollmentStatus } from '@app/queryTyping';

import { FieldInfoGroupField } from '@app/common/configurable-wizards';
import {
  FieldComponent,
  FieldLocator,
} from '@app/common/configurable-wizards/types';
import { isFieldInfoGroupField } from '@app/common/configurable-wizards/utils';
import { DEFAULT_COUNTRY } from '@app/common/types/locales';
import { findCountryByPhone } from '@app/common/utils/countries';

import { ActionListItem } from '@app/widgets/contacts-and-settings/components/ActionListItem';
import { DeleteModal } from '@app/widgets/contacts-and-settings/components/DeleteModal';
import { SectionHeader, SettingsSection } from '@app/widgets/contacts-and-settings/components/SettingsSection';
import { SubmitModal } from '@app/widgets/contacts-and-settings/components/SubmitModal';
import { useContactFieldMeta } from '@app/widgets/contacts-and-settings/hooks/useContactFieldMeta';
import { useUserSettingsTranslation } from '@app/widgets/contacts-and-settings/hooks/useUserSettingsTranslation';
import { setFieldValues } from '@app/widgets/contacts-and-settings/utils/fieldValues';
import { formatPhone } from '@app/widgets/contacts-and-settings/utils/labelFormatters';

import { useGetUserContactsSettingsQuery } from '../../queries/queryTyping/getUserContactSettings';
import { PhoneItemFields } from '../../types';

import { phoneFieldDetailsGroupsMeta as groupsMeta, INTL_NUMBER_FORMAT } from './constants';
import styles from './PhoneFieldDetails.pcss';
import { SmsEnrollment } from './SmsEnrollment';

export const PhoneFieldDetails: FieldComponent<FieldInfoGroupField> = ({
  field,
}) => {
  const { t } = useUserSettingsTranslation();
  const { data } = useGetUserContactsSettingsQuery();
  const { fieldMeta, isReadonly, isPrimary } = useContactFieldMeta(field);
  const phoneNumber = useMemo(() => formatPhone(fieldMeta), [fieldMeta]);

  const smsEnrollmentStatus: PhoneSmsEnrollmentStatus = fieldMeta.SmsEnrollmentStatus?.value.toUpperCase();
  const isSmsEnrollmentOn = PhoneSmsEnrollmentStatus.ENROLLED === smsEnrollmentStatus;
  const contactType = PhoneItemFields[field.id as keyof typeof PhoneItemFields];
  const currentGroup = groupsMeta[contactType];
  const isDeletePrimaryAllowed = data?.userContactSettings.isDeletePrimaryWorkPhoneEnabled
    && contactType === PhoneItemFields.WorkPhoneFieldGroup;

  const isSmsEnrollmentEnabled = useMemo(() => {
    const isEnabled = data?.userContactSettings.isAlertsBySmsEnabledForAllUsers || (
      data?.userContactSettings.isAllowedAlertChannelsSmsAlertEnabled && isSmsEnrollmentOn
    );

    const fieldFormat = fieldMeta?.MobilePhone?.maskFormat;
    const fieldValue = fieldMeta?.MobilePhone?.sValue;

    // enable/disable sms enrollment according to selected country
    if (isEnabled && fieldValue && fieldFormat === INTL_NUMBER_FORMAT) {
      return findCountryByPhone(fieldValue)?.countryCode === DEFAULT_COUNTRY;
    }

    return isEnabled;
  }, [data, fieldMeta, isSmsEnrollmentOn]);

  const contact = useMemo(() => ({
    contactId: fieldMeta?.UniqueId?.sValue,
    contactType: currentGroup?.type,
    contactEnrolled: isSmsEnrollmentOn,
  }), [fieldMeta, currentGroup]);

  const fieldGroup = useMemo(() => setFieldValues(field, {
    Enabled: 'true',
  }), [field]);

  return (
    <>
      <SettingsSection
        sectionHeader={(
          <>
            <SectionHeader
              isPrimary={isPrimary}
              title={phoneNumber}
              subTitle={t(
                'phone-field-details.primary-label|Primary phone label',
                'Primary phone',
              )}
            />
            {!isReadonly && (
              <SubmitModal
                field={fieldGroup}
                successMessage={currentGroup?.submitMessage}
                actionButton={(
                  <Button
                    data-stable-name="EditPhoneNumberButton"
                    disabled={isSmsEnrollmentOn}
                    look="icon"
                    icon={<Pen />}
                    aria-label={t(
                      'phone-fields-details.submit-phone.action-button.aria-label|Accessibility text for edit phone number button',
                      'Edit phone number',
                    )}
                  />
                )}
                dialogOptions={{
                  title: t(
                    'phone-field-details.edit-dialog.title|Edit phone number dialog title',
                    'Edit phone number',
                  ),
                  showClose: {
                    // eslint-disable-next-line i18next/no-literal-string
                    'aria-label': t(
                      'phone-field-details.edit-dialog.close-button.aria-label|Accessibility text for close button of edit phone dialog',
                      'Close edit phone dialog',
                    ),
                  },
                }}
              />
            )}
            {(!isPrimary || isDeletePrimaryAllowed) && !isReadonly && (
              <DeleteModal
                contact={contact}
                successMessage={currentGroup?.deleteMessage}
                dialogOptions={{
                  title: t(
                    'phone-field-details.delete-dialog.title|Delete phone number dialog title',
                    'Delete phone number',
                  ),
                }}
              >
                {
                  t(
                    // eslint-disable-next-line i18next/no-literal-string
                    'phone-field-details.delete-dialog.message|Delete phone number dialog message',
                    'Are you sure you want to delete phone number <strong>{{phoneNumber}}</strong> from your contact information?',
                    { phoneNumber },
                  )
                }
              </DeleteModal>
            )}
          </>
        )}
        sectionNote={t(
          'phone-field-details.phone.note|Phone number note in user settings phone details',
          'Your phone can be used to deliver important notifications, help you sign in, and more',
        )}
      />
      {!isReadonly && (
        <SettingsSection
          sectionHeader={t(
            'phone-field-details.preferences.title|Phone number preferences title',
            'Preferences',
          )}
        >
          <Menu className={styles.menu}>
            {[
              ...(contactType === PhoneItemFields.MobilePhoneFieldGroup && isSmsEnrollmentEnabled
                ? [<SmsEnrollment fieldMeta={fieldMeta} key={1} />]
                : []
              ),
              <ActionListItem
                data-stable-name="SetAsPrimaryPhoneButton"
                data={t(
                  'phone-fields-details.set-primary-button|Set phone number as primary button',
                  'Set as primary phone',
                )}
                key={2}
                getItemIcon={() => <Star />}
                aria-disabled={isPrimary}
              >
                <SubmitModal
                  // eslint-disable-next-line no-underscore-dangle
                  field={fieldGroup}
                  successMessage={currentGroup?.setPrimaryMessage}
                  markAsPrimary
                  dialogOptions={{
                    title: t(
                      'phone-fields-details.mark-primary-dialog-title.label|Text for a title on mark as primary phone number dialog',
                      'Set primary phone number',
                    ),
                    showClose: {
                      // eslint-disable-next-line i18next/no-literal-string
                      'aria-label': t(
                        'phone-fields-details.set-primary-dialog-close-button.aria-label|Text for a close button on mark as primary phone number dialog',
                        'Close set primary phone number',
                      ),
                    },
                  }}
                >
                  {t(
                    // eslint-disable-next-line i18next/no-literal-string
                    'phone-field-details.mark-primary-dialog.title-label|Text for a title on mark as primary phone number dialog',
                    'Are you sure you want to make phone number <strong>{{phoneNumber}}</strong> as primary?',
                    { phoneNumber },
                  )}
                </SubmitModal>
              </ActionListItem>,
            ]}
          </Menu>
        </SettingsSection>
      )}
    </>
  );
};

export const PhoneFieldDetailsLocator: FieldLocator = ({ field }) => (
  isFieldInfoGroupField(field) && field.id in PhoneItemFields
    ? PhoneFieldDetails
    : undefined
);
